import { EzStandardStateComponent } from '@template-ui/components/ez-standard-state/ez-standard-state.component';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { mapWorkflowRegistryDiagnosticError } from '../platform-admin/workflow-registry-diagnostics/platform-workflow-registry-diagnostics.models';
import { WorkflowChildWorkService } from './workflow-child-work.service';
import {
  childWorkStateLabel,
  type WorkflowChildConsumeRequest,
  type WorkflowChildLinkRead,
} from './workflow-child-work.models';
@Component({
  selector: 'phms-workflow-child-work-panel',
  standalone: true,
  imports: [
    EzStandardStateComponent,
    CommonModule,
    FormsModule,
    RouterLink,
    UiButtonDirective,
    UiControlDirective,
  ],
  template: `<section
    class="child-panel"
    aria-label="Related work"
    [attr.aria-busy]="loading || busy"
  >
    <header>
      <h2>Related work</h2>
      <button
        uiButton
        type="button"
        (click)="refresh()"
        [disabled]="busy || loading || interactionBlocked || previewMode"
      >
        {{ loaded ? 'Refresh related work' : 'View related work' }}
      </button>
    </header>
    <p *ngIf="previewMode">
      Synthetic preview only. No live related work is read or changed.
    </p>
    <p *ngIf="!previewMode && !loaded && !loading && !readErrorKind">
      Read this visit's related work and its recorded results.
    </p>
    <ui-standard-state
      *ngIf="loading || readErrorKind"
      [kind]="loading ? 'loading' : readErrorKind!"
      [message]="loading ? 'Loading related work…' : error"
    />
    <p *ngIf="error && !readErrorKind" role="alert">{{ error }}</p>
    <p *ngIf="notice" role="status">{{ notice }}</p>
    <nav *ngIf="loaded" aria-label="Related work views">
      <button
        uiButton
        type="button"
        [attr.aria-pressed]="view === 'summary'"
        (click)="view = 'summary'"
      >
        Summary ({{ rows.length }})</button
      ><button
        uiButton
        type="button"
        [attr.aria-pressed]="view === 'blocking'"
        (click)="view = 'blocking'"
      >
        Blocking ({{ blockingCount }})
      </button>
    </nav>
    <ui-standard-state
      *ngIf="loaded && !loading && !readErrorKind && !visibleRows.length"
      kind="empty"
      [message]="
        view === 'blocking'
          ? 'No related work is blocking this visit.'
          : 'No related work is recorded for this visit.'
      "
    />
    <article *ngFor="let link of visibleRows">
      <header>
        <h3>{{ link.label }}</h3>
        <strong>{{ stateLabel(link.state) }}</strong>
      </header>
      <p>
        {{
          link.mode === 'blocking'
            ? 'Required result'
            : link.mode === 'observational'
              ? 'Observation only'
              : 'Unavailable mode'
        }}
        · {{ link.isSatisfied ? 'Result accepted' : 'Result not accepted' }}
      </p>
      <p *ngIf="link.state === 'pending'">
        The related workflow is waiting to start.
      </p>
      <p *ngIf="link.childReference">{{ link.childReference }}</p>
      <a *ngIf="routeUrl(link.childRoute) as route" [routerLink]="route"
        >Open related workflow</a
      >
      <p *ngIf="link.outcomeLabel">Recorded result: {{ link.outcomeLabel }}</p>
      <p *ngIf="link.state === 'completed' && !link.isSatisfied">
        Completion does not accept the result or advance this visit.
      </p>
      <p *ngIf="link.issueCode" class="issue">
        Related work needs attention.<span *ngIf="link.mode === 'blocking'">
          Its required result remains unsatisfied.</span
        >
      </p>
      <button
        *ngIf="canSelect(link)"
        uiButton
        type="button"
        (click)="choose(link)"
        [disabled]="busy || interactionBlocked || stale"
      >
        Accept recorded result
      </button>
    </article>
    <section
      *ngIf="!selection && reason.trim()"
      aria-label="Retained related-work reason"
    >
      <p>
        Your reason is kept. Refresh and select a current result, or discard
        this input.
      </p>
      <label
        >Retained reason<textarea
          uiControl
          rows="2"
          maxlength="500"
          [ngModel]="reason"
          (ngModelChange)="reasonChanged($event)"
          [ngModelOptions]="standalone"
          [disabled]="busy || interactionBlocked"
        ></textarea></label
      ><button uiButton type="button" (click)="cancel()" [disabled]="busy">
        Discard retained reason
      </button>
    </section>
    <section
      *ngIf="selection as selected"
      aria-label="Accept related-work result"
    >
      <h3>Accept result · {{ selected.label }}</h3>
      <label
        >Reason<textarea
          uiControl
          rows="2"
          maxlength="500"
          [ngModel]="reason"
          (ngModelChange)="reasonChanged($event)"
          [ngModelOptions]="standalone"
          [disabled]="busy || interactionBlocked"
        ></textarea>
      </label>
      <p *ngIf="stale" role="status">
        This visit or result changed. Refresh and select the current result.
        Your reason is kept.
      </p>
      <div class="actions">
        <button
          uiButton
          type="button"
          (click)="submit()"
          [disabled]="busy || interactionBlocked || stale || !reason.trim()"
        >
          {{ busy ? 'Saving…' : 'Accept result' }}</button
        ><button uiButton type="button" (click)="cancel()" [disabled]="busy">
          Cancel result acceptance
        </button>
      </div>
    </section>
  </section>`,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
      }
      .child-panel {
        display: grid;
        gap: 0.7rem;
        border-top: 1px solid var(--custom-border-color);
        padding-top: 0.75rem;
      }
      header,
      .actions,
      nav {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.5rem;
      }
      header {
        justify-content: space-between;
      }
      h2,
      h3,
      p {
        margin: 0;
      }
      article,
      section section {
        display: grid;
        gap: 0.5rem;
        border: 1px solid var(--custom-border-color);
        border-radius: var(--custom-border-radius, 4px);
        padding: 0.7rem;
      }
      label {
        display: grid;
        gap: 0.35rem;
      }
      textarea {
        width: 100%;
      }
      [role='alert'],
      .issue {
        color: var(--custom-text-color-error);
      }
    `,
  ],
})
export class WorkflowChildWorkPanelComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) workflowId = 0;
  @Input() occurrenceId: number | null = null;
  @Input() previewMode = false;
  @Input() autoLoad = false;
  @Input() viewedETag: string | null = null;
  @Input() interactionBlocked = false;
  @Output() busyChange = new EventEmitter<boolean>();
  @Output() pendingInputChange = new EventEmitter<boolean>();
  @Output() changed = new EventEmitter<void>();
  @Output() inspectionRequested = new EventEmitter<{
    workflowId: number;
    occurrenceId: number;
  }>();
  private readonly api = inject(WorkflowChildWorkService);
  private readonly router = inject(Router);
  private readRequest?: Subscription;
  private consumeRequest?: Subscription;
  private generation = 0;
  private context = '';
  private eTag = '';
  private parentETag: string | null = null;
  private intent: {
    signature: string;
    key: string;
    request: WorkflowChildConsumeRequest;
  } | null = null;
  rows: WorkflowChildLinkRead[] = [];
  view: 'summary' | 'blocking' = 'summary';
  loaded = false;
  loading = false;
  busy = false;
  stale = false;
  error = '';
  readErrorKind: 'error' | 'forbidden' | null = null;
  notice = '';
  reason = '';
  selection: {
    label: string;
    request: Omit<WorkflowChildConsumeRequest, 'reason'>;
  } | null = null;
  readonly standalone = { standalone: true };
  stateLabel = childWorkStateLabel;
  get blockingCount() {
    return this.rows.filter(
      (link) => link.mode === 'blocking' && !link.isSatisfied,
    ).length;
  }
  get visibleRows() {
    return this.view === 'blocking'
      ? this.rows.filter(
          (link) => link.mode === 'blocking' && !link.isSatisfied,
        )
      : this.rows;
  }
  safeRoute(route: string | null): string | null {
    return route && /^\/workflows\/[1-9]\d*(?:\?[^#]*)?$/.test(route)
      ? route
      : null;
  }
  routeUrl(route: string | null) {
    const safe = this.safeRoute(route);
    return safe ? this.router.parseUrl(safe) : null;
  }
  ngOnChanges() {
    const context = JSON.stringify([
      this.workflowId,
      this.occurrenceId,
      this.previewMode,
    ]);
    if (context === this.context) {
      const parentChanged = this.parentETag !== this.viewedETag;
      this.parentETag = this.viewedETag;
      if (
        parentChanged &&
        this.loaded &&
        this.viewedETag &&
        this.eTag !== this.viewedETag
      ) {
        this.stale = true;
        this.intent = null;
      }
      return;
    }
    this.context = context;
    this.parentETag = this.viewedETag;
    this.generation++;
    this.readRequest?.unsubscribe();
    this.consumeRequest?.unsubscribe();
    this.rows = [];
    this.loaded = false;
    this.loading = false;
    this.busy = false;
    this.stale = false;
    this.error = '';
    this.readErrorKind = null;
    this.notice = '';
    this.eTag = '';
    this.view = 'summary';
    this.selection = null;
    this.reason = '';
    this.intent = null;
    this.busyChange.emit(false);
    this.pendingInputChange.emit(false);
    if (this.autoLoad && !this.previewMode) this.refresh();
  }
  ngOnDestroy() {
    this.generation++;
    this.readRequest?.unsubscribe();
    this.consumeRequest?.unsubscribe();
    this.busyChange.emit(false);
    this.pendingInputChange.emit(false);
  }
  refresh() {
    if (
      this.previewMode ||
      this.workflowId < 1 ||
      !this.occurrenceId ||
      this.busy ||
      this.interactionBlocked
    )
      return;
    this.inspectionRequested.emit({
      workflowId: this.workflowId,
      occurrenceId: this.occurrenceId,
    });
    const generation = ++this.generation;
    this.readRequest?.unsubscribe();
    this.rows = [];
    this.loaded = false;
    this.loading = true;
    this.error = '';
    this.readErrorKind = null;
    this.notice = '';
    this.selection = null;
    this.intent = null;
    this.stale = false;
    this.pendingInputChange.emit(!!this.reason.trim());
    this.readRequest = this.api
      .read(this.workflowId, this.occurrenceId)
      .subscribe({
        next: (snapshot) => {
          if (generation !== this.generation) return;
          if (
            snapshot.workflowId !== this.workflowId ||
            snapshot.occurrenceId !== this.occurrenceId
          ) {
            this.loading = false;
            this.loaded = false;
            this.readErrorKind = 'error';
            this.error =
              'The related-work response belongs to another visit. Refresh this visit.';
            return;
          }
          this.rows = snapshot.links;
          this.eTag = snapshot.eTag;
          this.loading = false;
          this.loaded = true;
          // This explicit authorized read is the current view of the exact parent visit.
          // Its envelope supplies the CAS token even when another workspace panel is older.
          this.stale = false;
        },
        error: (error: unknown) => {
          if (generation !== this.generation) return;
          this.loading = false;
          this.loaded = false;
          this.rows = [];
          this.readErrorKind =
            error instanceof HttpErrorResponse &&
            [401, 403].includes(error.status)
              ? 'forbidden'
              : 'error';
          this.error =
            this.readErrorKind === 'forbidden'
              ? 'You do not have permission to view related work for this visit.'
              : 'Related work could not be loaded. Try again; your entered reason is kept.';
          if (
            error instanceof HttpErrorResponse &&
            [401, 403].includes(error.status)
          )
            this.clearProtectedInput();
        },
      });
  }
  canSelect(link: WorkflowChildLinkRead) {
    return (
      link.state === 'completed' &&
      link.canConsume &&
      !link.isSatisfied &&
      !!link.outcomeId &&
      Number.isSafeInteger(link.outcomeRevision) &&
      link.outcomeRevision! > 0
    );
  }
  choose(link: WorkflowChildLinkRead) {
    if (
      this.previewMode ||
      this.busy ||
      this.interactionBlocked ||
      this.stale ||
      !this.occurrenceId ||
      !this.rows.includes(link) ||
      !this.canSelect(link)
    )
      return;
    this.selection = {
      label: link.label,
      request: {
        occurrenceId: this.occurrenceId,
        expectedETag: this.eTag,
        linkId: link.id,
        expectedRevision: link.revision,
        expectedOutcomeId: link.outcomeId!,
        expectedOutcomeRevision: link.outcomeRevision!,
      },
    };
    this.intent = null;
    this.error = '';
    this.notice = '';
    this.pendingInputChange.emit(true);
  }
  reasonChanged(reason: string) {
    if (this.busy || this.interactionBlocked) return;
    this.reason = reason;
    this.intent = null;
    this.pendingInputChange.emit(!!this.selection || !!reason.trim());
  }
  cancel() {
    if (this.busy) return;
    this.selection = null;
    this.reason = '';
    this.intent = null;
    this.notice = '';
    this.pendingInputChange.emit(false);
  }
  submit() {
    if (
      this.previewMode ||
      this.busy ||
      this.interactionBlocked ||
      this.stale ||
      !this.selection ||
      !this.occurrenceId ||
      !this.reason.trim() ||
      this.reason.trim().length > 500
    )
      return;
    const request = { ...this.selection.request, reason: this.reason.trim() };
    const signature = JSON.stringify([this.workflowId, request]);
    if (this.intent?.signature !== signature)
      this.intent = {
        signature,
        key: crypto.randomUUID(),
        request: structuredClone(request),
      };
    const generation = ++this.generation;
    this.readRequest?.unsubscribe();
    this.busy = true;
    this.error = '';
    this.notice = '';
    this.busyChange.emit(true);
    this.consumeRequest = this.api
      .consume(this.workflowId, this.intent.request, this.intent.key)
      .subscribe({
        next: (result) => {
          if (generation !== this.generation) return;
          this.busy = false;
          this.busyChange.emit(false);
          if (
            result?.link?.id !== request.linkId ||
            typeof result?.eTag !== 'string' ||
            !result.eTag.trim()
          ) {
            this.stale = true;
            this.intent = null;
            this.error =
              'The returned result could not be matched to this selection. Refresh current related work; your reason is kept.';
            return;
          }
          this.rows = this.rows.map((link) =>
            link.id === result.link.id ? result.link : link,
          );
          this.eTag = result.eTag;
          this.selection = null;
          this.reason = '';
          this.intent = null;
          this.pendingInputChange.emit(false);
          this.notice =
            'The recorded result was accepted. This command does not advance the visit.';
          this.changed.emit();
        },
        error: (error: unknown) => {
          if (generation !== this.generation) return;
          this.busy = false;
          this.busyChange.emit(false);
          this.error = this.errorMessage(error);
          if (
            error instanceof HttpErrorResponse &&
            [401, 403].includes(error.status)
          ) {
            this.rows = [];
            this.loaded = false;
            this.clearProtectedInput();
          }
          if (
            error instanceof HttpErrorResponse &&
            [409, 412, 428].includes(error.status)
          ) {
            this.stale = true;
            this.intent = null;
          }
          if (error instanceof HttpErrorResponse && error.status === 0)
            this.notice =
              'The result is uncertain. Retrying unchanged input uses the same request identity.';
        },
      });
  }
  private clearProtectedInput() {
    this.selection = null;
    this.intent = null;
    this.reason = '';
    this.pendingInputChange.emit(false);
  }
  private errorMessage(error: unknown) {
    const display = mapWorkflowRegistryDiagnosticError(error);
    return `${display.title}: ${display.messages.join(' ')}`;
  }
}
